import { Component } from '@theme/component';

/**
 * Autoplay controller for the FZ Hero slideshow. Replaces Horizon's built-in autoplay so the
 * progress indicator and the slide timer always stay in sync.
 *
 * Rotation holds while:
 * - the visitor interacted (click, tap, swipe, key) — resumes after `resume-delay` seconds
 *   without further interaction (0 = never resume),
 * - the mouse moves onto a button or the slideshow controls (cleared when the resume timer ends),
 * - keyboard focus is inside the slideshow,
 * - the slideshow is off-screen, the tab is hidden, or the theme editor is open.
 * The pause button (WCAG 2.2.2) stops rotation until play is pressed. Visitors who prefer
 * reduced motion start paused.
 *
 * State is exposed as the `fz-autoplay` attribute on <slideshow-component>:
 * `running` | `holding` | `paused`. CSS drives the progress indicator from it.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} pauseButton
 * @property {HTMLButtonElement} playButton
 *
 * @extends {Component<Refs>}
 */
class FzHeroAutoplay extends Component {
  requiredRefs = ['pauseButton', 'playButton'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<string>} */
  #holds = new Set();

  #userPaused = false;

  #canHover = window.matchMedia('(hover: hover)');

  #ignoreHoverUntilMove = false;

  /** @type {number | undefined} */
  #slideTimer;

  /** @type {number | undefined} */
  #resumeTimer;

  connectedCallback() {
    super.connectedCallback();

    const slideshow = this.slideshow;
    if (!slideshow) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    slideshow.addEventListener('pointerdown', this.#handleInteraction, { signal });
    slideshow.addEventListener('keydown', this.#handleInteraction, { signal });
    slideshow.addEventListener('pointerover', this.#handlePointerMove, { signal });
    slideshow.addEventListener('pointerout', this.#handlePointerMove, { signal });
    slideshow.addEventListener('pointermove', this.#handlePointerMove, { signal });
    slideshow.addEventListener('focusin', this.#handleFocusIn, { signal });
    slideshow.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    this.#observer = new IntersectionObserver(([entry]) => this.#toggleHold('offscreen', !entry?.isIntersecting));
    this.#observer.observe(slideshow);

    if (/** @type {any} */ (window).Shopify?.designMode) this.#holds.add('editor');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#userPaused = true;

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#slideTimer);
    clearTimeout(this.#resumeTimer);
  }

  /** @returns {(HTMLElement & { next: () => void }) | null} */
  get slideshow() {
    return /** @type {any} */ (this.closest('slideshow-component'));
  }

  get delay() {
    return (Number(this.getAttribute('delay')) || 6) * 1000;
  }

  get resumeDelay() {
    return Number(this.getAttribute('resume-delay')) * 1000;
  }

  pause() {
    this.#userPaused = true;
    this.#update();
    this.refs.playButton.focus();
  }

  play() {
    this.#userPaused = false;
    this.#holds.delete('interaction');
    clearTimeout(this.#resumeTimer);
    this.#update();
    this.refs.pauseButton.focus();
  }

  #update() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    clearTimeout(this.#slideTimer);

    const state = this.#userPaused ? 'paused' : this.#holds.size ? 'holding' : 'running';

    if (state !== 'running') {
      slideshow.setAttribute('fz-autoplay', state);
      slideshow.setAttribute('aria-live', 'polite');
      return;
    }

    // Restart the progress animation in sync with the slide timer.
    slideshow.setAttribute('fz-autoplay', 'holding');
    void slideshow.offsetWidth;
    slideshow.setAttribute('fz-autoplay', 'running');
    slideshow.setAttribute('aria-live', 'off');

    this.#slideTimer = window.setTimeout(() => {
      slideshow.next();
      this.#update();
    }, this.delay);
  }

  /**
   * @param {string} reason
   * @param {boolean} active
   */
  #toggleHold(reason, active) {
    if (active === this.#holds.has(reason)) return;

    if (active) {
      this.#holds.add(reason);
    } else {
      this.#holds.delete(reason);
    }

    this.#update();
  }

  /** @param {Event} event */
  #handleInteraction = (event) => {
    if (event instanceof KeyboardEvent && ['Tab', 'Shift', 'Alt', 'Control', 'Meta'].includes(event.key)) return;
    if (event.target instanceof Element && event.target.closest('fz-hero-autoplay')) return;

    clearTimeout(this.#resumeTimer);
    this.#toggleHold('interaction', true);

    if (this.resumeDelay <= 0) {
      this.#userPaused = true;
      this.#update();
      return;
    }

    this.#resumeTimer = window.setTimeout(() => {
      // The pointer often still rests on the control that was clicked; don't let that block resuming.
      this.#holds.delete('hover');
      this.#ignoreHoverUntilMove = true;
      this.#toggleHold('interaction', false);
    }, this.resumeDelay);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.#canHover.matches) return;

    // Browsers re-fire pointerover after layout changes under a resting mouse; only real movement counts.
    if (event.type === 'pointermove') {
      if (!this.#ignoreHoverUntilMove || (!event.movementX && !event.movementY)) return;
      this.#ignoreHoverUntilMove = false;
    } else if (this.#ignoreHoverUntilMove) {
      return;
    }

    const target = event.type === 'pointerout' ? event.relatedTarget : event.target;
    const over = target instanceof Element && Boolean(target.closest('a, button, .fz-hero__indicators, slideshow-arrows'));
    this.#toggleHold('hover', over && Boolean(this.slideshow?.contains(target)));
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#toggleHold('focus', true);
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (this.slideshow?.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    this.#toggleHold('focus', false);
  };

  #handleVisibilityChange = () => this.#toggleHold('hidden', document.hidden);
}

if (!customElements.get('fz-hero-autoplay')) {
  customElements.define('fz-hero-autoplay', FzHeroAutoplay);
}
